/**
 * ## AdvancedItemsProvider.tsx ##
 * This file contains AdvancedItemsProvider component
 * @packageDocumentation
 */

import React, { PropsWithChildren } from 'react';

import { WithDeleted } from '@common/typescript/objects/WithDeleted';
import {
	ItemsProvider,
	useItemsProviderContext,
	ItemsProviderProps,
	createItemsProviderContext,
	defaultTransformFiltersBeforeHandleUrl,
	SortingDirection,
	ItemsProviderContextActions,
	WithKey,
	ItemsProviderContext,
	LoadData,
	Edits,
	ColumnFilter,
} from '@common/react/components/Core/ItemsProvider/ItemsProvider';
import { BaseParams } from '@common/typescript/objects/BaseParams';
import { getSearchParamsFromUrl } from '@common/react/utils/FIltersParamsFromUrl/FiltersParamsFromUrl';
import { List } from '@common/typescript/objects/List';

export type { BaseFilters } from '@common/react/components/Core/ItemsProvider/ItemsProvider';

/**
 * AdvancedItemsProvider component props
 *
 * @interface AdvancedItemsProviderProps
 * @typeParam T - T Any WithDeleted entity
 * @typeParam TFilter
 * @typeParam TResponse
 */
export interface AdvancedItemsProviderProps<T extends WithDeleted,
	TFilter extends BaseParams = BaseParams,
	TResponse extends List<T> = List<T>> extends ItemsProviderProps<T, TFilter, TResponse> {
		/**
		 * determines whether to add an item from addItem to the array based on filters
		 * @param item         - element being checked
		 * @param filters      - current filters
		 * @return Promise<boolean> | boolean
		 */
		filterHandler: (item: T, filters: TFilter) => boolean | Promise<boolean>;
		/**
		 * default sorting (when no sorting is specified in the filters)
		 */
		defaultSort?: [string, SortingDirection];
		/**
		 * defines the sort order of elements after adding a new element via addItem
		 * @param item1         - first element to be sorted
		 * @param item2         - second element to be sorted
		 * @param filters       - current filters
		 * @return number
		 */
		sortHandler?: (item1: T, item2: T, filters: TFilter) => number; // return number as function for Array.sort
	}

interface AdvancedItemsHandlerProps<T extends WithKey, TFilter extends BaseParams = BaseParams> {
	filterHandler: (item: T, filters: TFilter) => Promise<boolean> | boolean;
	defaultSort?: [string, SortingDirection];
	sortHandler?: (item1: T, item2: T, filters: TFilter) => number;
	add?: (items: Array<T>) => T;
	addedFirst?: boolean;
}

export interface AdvancedItemsProviderContextActions<T extends WithKey,
	TFilter extends BaseParams = BaseParams,
	TResponse extends List<T> = List<T>> extends ItemsProviderContextActions<T, TFilter, TResponse> {}

export const checkFilterProp = (value: Array<any> | number | string | boolean | undefined | null) => {
	if (value === undefined || value === null || value === '') {
		return false;
	}
	if (typeof value === 'number') {
		return value > -1;
	}
	if (typeof value === 'string') {
		if (!isNaN(+value)) {
			return +value > -1;
		}
	}
	if (Array.isArray(value)) {
		return value.length > 0;
	}
	return true;
};

export const findId = <T, >(array: Array<T>, id: number | undefined): T => {
	return array.find((i) => +i === +(id || -1));
};

const defaultSortHandler = <T, TFilter extends { column?: Array<ColumnFilter> }>(
	item1: T,
	item2: T,
	filters: TFilter,
	defaultSort: [string, SortingDirection],
) => {
	const { column } = filters;
	if (column?.[0]) {
		const { caption: prop, direction } = column[0];
		if (direction === SortingDirection.Ascending) {
			return item1[prop] - item2[prop];
		}
		if (direction === SortingDirection.Descending) {
			return item2[prop] - item1[prop];
		}
	}
	const [prop, type] = defaultSort;
	if (type === SortingDirection.Ascending) {
		return item1[prop] - item2[prop];
	}
	if (type === SortingDirection.Descending) {
		return item2[prop] - item1[prop];
	}
	return 0;
};

export const useAdvancedItemsProviderContext = <T extends WithKey,
	TFilters extends BaseParams = BaseParams,
	TResponse extends List<T> = List<T>,
>(required?: boolean) => useItemsProviderContext<T,
	TFilters, TResponse, AdvancedItemsProviderContextActions<T, TFilters, TResponse>>(required);

const AdvancedItemsHandler: <T extends WithDeleted, TFilter extends BaseParams = BaseParams, TResponse extends List<any> = List<T>>(
	p: PropsWithChildren<AdvancedItemsHandlerProps<T, TFilter>>
) => React.ReactElement = <T extends WithDeleted, TFilter extends BaseParams = BaseParams, TResponse extends List<any> = List<T>>(
	p: PropsWithChildren<AdvancedItemsHandlerProps<T, TFilter>>,
) => {
	const {
		children,
		filterHandler,
		sortHandler,
		defaultSort = ['sortOrder', SortingDirection.Ascending],
	} = p;
	const ItemsContext = createItemsProviderContext<T, TFilter, TResponse, AdvancedItemsProviderContextActions<T, TFilter, TResponse>>();

	const context = useItemsProviderContext<T, TFilter, TResponse>();

	const {
		state: {
			items: itemsProp, pagination, filters, loading, loaders, edits, errors, error,
		},
		actions: {
			setItems, reload, load, setEdit,
		},
	} = context;

	const [id, setId] = React.useState(-1);

	const value: ItemsProviderContext<T, TFilter, TResponse, AdvancedItemsProviderContextActions<T, TFilter, TResponse>> = React.useMemo(() => {
		const { pageSize, current } = pagination;

		const isFirstPage = current === 1;

		const items = itemsProp.slice(isFirstPage ? 0 : 1)
			.slice(0, pageSize - 2 + Object.keys(edits)
				.filter((key) => +key < 0).length);

		const handledPagination = {
			...(pagination || { current: 1, pageSize: 10, total: 0 }),
			pageSize: pagination.pageSize ? pagination.pageSize - 2 : 10,
		};

		const handleReload = (params?: LoadData, resetFilters?: boolean) => {
			const pageSize = params?.pageSize || pagination.pageSize - 2;
			const current = params?.current || pagination.current;
			const isFirstPage = current === 1;

			return reload({
				...params,
				pageSize: params?.pageSize ? params.pageSize + 2 : undefined,
				offset: isFirstPage ? 0 : pageSize * (current - 1) - 1,
			}, resetFilters);
		};

		const addItem = (item: T) => {
			Promise.resolve(filterHandler(item, filters)).then((res) => {
				if (res) {
					const newItems = sortHandler || defaultSort
						? itemsProp.concat(item)
							.sort((item1, item2) =>
								(sortHandler ? sortHandler(item1, item2, filters)
									: defaultSortHandler(item1, item2, filters, defaultSort)))
						: itemsProp.concat(item);
					if (newItems.length > pagination.pageSize) {
						newItems.pop();
					}
					setItems(newItems);
				}
			});
		};

		const add = (item?: Partial<T>, useIdFromItem = false, isEdit: boolean = true) => {
			if (item && useIdFromItem && !isEdit) {
				addItem(item as T);
			}
			setId((id) => id - 1);
			const newId = (useIdFromItem ? item?.id : id) ?? id;
			const newItem = (item ? { ...item, id: newId } : { ...p.add?.(itemsProp), id: newId }) as T;

			const newItems = p.addedFirst
				? isFirstPage ? [newItem].concat(itemsProp) : [...itemsProp.slice(0, 1), newItem, ...itemsProp.slice(1)]
				: [
					...itemsProp.slice(0, itemsProp.length - (isFirstPage ? 2 : 1)),
					newItem,
					...itemsProp.slice(itemsProp.length - (isFirstPage ? 2 : 1)),
				];

			setItems(newItems);

			if (isEdit) setEdit(newItem);

			return newItem;
		};

		const handleLoad = (
			data?: LoadData,
			...rest: [
				silent?: boolean,
				concatResult?: boolean,
				resetFilters?: boolean,
				resetFilters?: boolean,
				useResult?: { use: boolean },
				edits?: Edits<T>,
			]
		) => {
			return load(data?.pageSize ? { ...data, pageSize: data.pageSize + 2 } : data, ...rest);
		};

		return {
			state: {
				...context.state,
				items,
				pagination: handledPagination,
				advancedItems: itemsProp,
			},
			actions: {
				...context.actions,
				load: handleLoad,
				reload: handleReload,
				addItem: add,
			},
		};
	}, [itemsProp, pagination, loading, filters, loaders, edits, errors, error, p.add]);

	return (
		<ItemsContext.Provider value={value}>
			{typeof children === 'function' ? children(value) : children}
		</ItemsContext.Provider>
	);
};

/**
 * AdvancedItemsProvider component.
 *
 * will override itemsProvider 'addItem' method with implementation
 * that check filters and apply sorts before insert item
 * also load and store one item from page before current and one from page after current
 *
 * it should be used when
 *  - we need insert, update and remove item with filters applying (with inner ItemsProviderSynchronizer)
 *  - we need sort items and move items to the next or previous pages (with inner EditableTableWithSortable)
 *
 * usage examples:
 *  - <AdvancedItemsProvider<T, TFilter> type="someType" filterHandler={() => true}>{React.ReactNode}</AdvancedItemsProvider>
 *  - <AdvancedItemsProvider<T, TFilter> type="someType" filterHandler={() => true}>{(context) => React.ReactNode}</AdvancedItemsProvider>
 *  - <AdvancedItemsProvider<T, TFilter> type="someType" filterHandler={() => true} render={(item, props) => SingleItem} />
 *
 * @typeParam T - T Any {WithKey}
 * @typeParam TFilter
 * @typeParam TResponse
 * @param p - AdvancedItemsProviderProps
 * @type {React.FC<AdvancedItemsProviderProps>}
 * @returns React.ReactElement
 */
const AdvancedItemsProvider: <T extends WithKey, TFilter extends BaseParams = BaseParams, TResponse extends List<T> = List<T>>(
	p: AdvancedItemsProviderProps<T, TFilter, TResponse>
) => React.ReactElement<T> = <T extends WithDeleted, TFilter extends BaseParams = BaseParams, TResponse extends List<T> = List<T>>(
	p: AdvancedItemsProviderProps<T, TFilter, TResponse>,
) => {
	const { children } = p;

	const pagination = {
		...(p.pagination || { current: 1, pageSize: 12, total: 0 }),
		pageSize: p.pagination?.pageSize ? p.pagination.pageSize + 2 : 12,
		total: p.pagination?.total ?? p.rows?.count ?? 0,
	};

	const transformFiltersBeforeHandleUrl = (filters: TFilter) => {
		const urlFilters = defaultTransformFiltersBeforeHandleUrl({
			...filters,
			pageSize: filters.pageSize ? filters.pageSize - 2 : undefined,
			count: filters.pageSize || filters.count ? (filters.count || filters.pageSize) - 2 : undefined,
		});

		return p.transformFiltersBeforeHandleUrl ? p.transformFiltersBeforeHandleUrl(urlFilters) : urlFilters;
	};

	const searchParamsFromUrl = (location: { search: string }, prefix?: string) => {
		const params = getSearchParamsFromUrl(location, prefix);
		const pageSize = params[`${prefix || ''}pageSize`];

		return {
			...params,
			[`${prefix || ''}pageSize`]: pageSize ? pageSize + 2 : 12,
		} as TFilter;
	};

	return (
		<ItemsProvider<T, TFilter, TResponse>
			{...p}
			transformFiltersBeforeHandleUrl={transformFiltersBeforeHandleUrl}
			pagination={pagination}
			searchParamsFromUrl={searchParamsFromUrl}
		>
			<AdvancedItemsHandler<T, TFilter, TResponse>
				defaultSort={p.defaultSort}
				filterHandler={p.filterHandler}
				sortHandler={p.sortHandler}
				addedFirst={p.addedFirst}
				add={p.add}
			>
				{children}
			</AdvancedItemsHandler>
		</ItemsProvider>
	);
};

export default AdvancedItemsProvider;
